/**
 * Gesto de arrastar horizontal do card (spec 9.1): direita conclui, esquerda
 * abre adiar/excluir.
 *
 * Pointer Events, e nao Touch Events: a mesma implementacao cobre dedo, mouse
 * e caneta, e `setPointerCapture` garante que o gesto continue chegando mesmo
 * se o dedo sair do card no meio do movimento.
 *
 * O swipe e SEMPRE um atalho, nunca o unico caminho: toda acao alcancavel por
 * gesto tem botao ou checkbox equivalente. Gesto nao e anunciado por leitor de
 * tela nem alcancado por teclado (spec 10.5).
 */

import {
  useCallback,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

export interface UseSwipeOptions {
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  /** Distancia minima, em px, para o gesto valer. */
  threshold?: number;
}

export interface SwipeHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

const DEFAULT_THRESHOLD = 72;

/** Acima deste desvio vertical o gesto e rolagem da lista, nao swipe. */
const VERTICAL_TOLERANCE = 16;

/**
 * Pointer Capture e um REFORCO do gesto, nao a condicao dele: sem captura o
 * swipe ainda funciona, apenas perde o rastreamento se o dedo sair do card.
 * Nem todo ambiente implementa a API — jsdom nao implementa —, e chamar o
 * metodo direto transformaria um clique comum em excecao. Por isso a deteccao.
 */
function capturePointer(element: Element, pointerId: number): void {
  if (typeof element.setPointerCapture !== 'function') return;
  element.setPointerCapture(pointerId);
}

function releasePointer(element: Element, pointerId: number): void {
  if (
    typeof element.hasPointerCapture !== 'function' ||
    typeof element.releasePointerCapture !== 'function'
  ) {
    return;
  }
  if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
}

export function useSwipe(options: UseSwipeOptions): {
  offset: number;
  swiping: boolean;
  handlers: SwipeHandlers;
} {
  const { onSwipeRight, onSwipeLeft, threshold = DEFAULT_THRESHOLD } = options;

  const [offset, setOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const origin = useRef<{ x: number; y: number } | null>(null);
  // `null` = ainda indeciso; a direcao trava na primeira amostra conclusiva
  // para o card nao "tremer" entre rolar e arrastar.
  const horizontal = useRef<boolean | null>(null);

  const reset = useCallback(() => {
    origin.current = null;
    horizontal.current = null;
    setOffset(0);
    setSwiping(false);
  }, []);

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    // Mouse so arrasta com o botao principal; o direito abre menu de contexto.
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    origin.current = { x: event.clientX, y: event.clientY };
    horizontal.current = null;
  }, []);

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const start = origin.current;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (horizontal.current === null) {
      if (Math.abs(dx) < VERTICAL_TOLERANCE && Math.abs(dy) < VERTICAL_TOLERANCE) return;

      horizontal.current = Math.abs(dx) > Math.abs(dy);
      if (!horizontal.current) {
        // Movimento vertical: solta o gesto e deixa a lista rolar.
        origin.current = null;
        return;
      }

      capturePointer(event.currentTarget, event.pointerId);
      setSwiping(true);
    }

    setOffset(dx);
  }, []);

  const finish = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const start = origin.current;
      const wasHorizontal = horizontal.current === true;
      const dx = start ? event.clientX - start.x : 0;

      releasePointer(event.currentTarget, event.pointerId);
      reset();

      if (!wasHorizontal || Math.abs(dx) < threshold) return;
      if (dx > 0) onSwipeRight?.();
      else onSwipeLeft?.();
    },
    [onSwipeLeft, onSwipeRight, reset, threshold],
  );

  return {
    offset,
    swiping,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: () => reset(),
    },
  };
}
